/* ============================================================
   9MM WEBGL — hero energy particle field (Three.js r128)
   Two counter-rotating point clouds (red / cyan) living behind the
   hero content. Reacts to mouse, scroll, and the Body/Mind world.
   Pauses off-screen. Skips on reduced-motion or missing WebGL.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  function start() {
    if (!window.THREE) return;
    var THREE = window.THREE;

    var hero = document.querySelector('.nm-hero-slider') || document.querySelector('.nm-hero');
    var host = hero && (hero.querySelector('.nm-hero-bg') || hero);
    if (!host) return;

    var isMobile = window.innerWidth <= 768;
    var COUNT = isMobile ? 650 : 2200;

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'low-power' });
    } catch (e) { return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.className = 'nm-hero-particles';
    host.appendChild(renderer.domElement);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(60, 1, 0.1, 60);
    camera.position.z = 12;

    function makeCloud(colorHex, spread) {
      var geo = new THREE.BufferGeometry();
      var n = Math.floor(COUNT / 2);
      var pos = new Float32Array(n * 3);
      for (var i = 0; i < n; i++) {
        pos[i * 3] = (Math.random() - 0.5) * spread.x;
        pos[i * 3 + 1] = (Math.random() - 0.5) * spread.y;
        pos[i * 3 + 2] = (Math.random() - 0.5) * spread.z;
      }
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      var mat = new THREE.PointsMaterial({
        color: colorHex,
        size: isMobile ? 0.06 : 0.045,
        transparent: true,
        opacity: 0.75,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: true
      });
      var points = new THREE.Points(geo, mat);
      scene.add(points);
      return points;
    }

    var red = makeCloud(0xff2a2a, { x: 30, y: 16, z: 10 });
    var cyan = makeCloud(0x00edef, { x: 30, y: 16, z: 10 });

    /* deeper inks in light theme so particles read on white */
    function applyTheme() {
      var light = document.documentElement.getAttribute('data-theme') === 'light';
      red.material.color.setHex(light ? 0xd40f0f : 0xff2a2a);
      cyan.material.color.setHex(light ? 0x00838f : 0x00edef);
      red.material.blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;
      cyan.material.blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;
      red.material.needsUpdate = true;
      cyan.material.needsUpdate = true;
    }
    applyTheme();
    if ('MutationObserver' in window) {
      new MutationObserver(applyTheme)
        .observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }

    /* world state drives cloud dominance */
    function applyWorld(mode, immediate) {
      var targets = mode === 'mind'
        ? { red: 0.18, cyan: 0.85 }
        : { red: 0.85, cyan: 0.18 };
      if (window.gsap && !immediate) {
        window.gsap.to(red.material, { opacity: targets.red, duration: 0.9, ease: 'power2.out' });
        window.gsap.to(cyan.material, { opacity: targets.cyan, duration: 0.9, ease: 'power2.out' });
      } else {
        red.material.opacity = targets.red;
        cyan.material.opacity = targets.cyan;
      }
    }
    applyWorld(document.body.getAttribute('data-mode') || 'body', true);
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        applyWorld(document.body.getAttribute('data-mode') || 'body');
      }).observe(document.body, { attributes: true, attributeFilter: ['data-mode'] });
    }

    /* size to host */
    function resize() {
      var w = host.clientWidth || 1, h = host.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener('resize', resize);

    /* mouse parallax (desktop) */
    var mx = 0, my = 0;
    if (!isMobile) {
      document.addEventListener('mousemove', function (e) {
        mx = (e.clientX / window.innerWidth - 0.5);
        my = (e.clientY / window.innerHeight - 0.5);
      });
    }

    /* render only while hero is on screen */
    var visible = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0 }).observe(hero);
    }

    var clock = new THREE.Clock();
    function tick() {
      requestAnimationFrame(tick);
      if (!visible || document.hidden) return;
      var t = clock.getElapsedTime();
      red.rotation.y = t * 0.045;
      red.rotation.x = Math.sin(t * 0.12) * 0.06;
      cyan.rotation.y = -t * 0.06;
      cyan.rotation.x = Math.cos(t * 0.1) * 0.05;
      /* breathing */
      var b = 1 + Math.sin(t * 0.5) * 0.03;
      red.scale.set(b, b, b);
      cyan.scale.set(2 - b, 2 - b, 2 - b);
      /* mouse + scroll influence */
      camera.position.x += (mx * 2.2 - camera.position.x) * 0.04;
      camera.position.y += (-my * 1.4 - (window.scrollY || 0) * 0.0012 - camera.position.y) * 0.04;
      camera.lookAt(scene.position);
      renderer.render(scene, camera);
    }
    tick();

    initCan3D(hero, isMobile);
  }

  /* entry point for every page — start() bails without a hero,
     so the anatomy & product scenes must not live behind it */
  function bootAll() {
    var isMobile = window.innerWidth <= 768;
    start();
    initScrub(isMobile);
    /* product 3D is now native Shopify (model_viewer_tag + AR) when the merchant
       uploads a GLB to the product media — no custom floating viewer / duplicate.
       (the old custom initProductViewer() was removed as dead code) */
  }

  /* small shared texture cache for the extra scenes */
  function loadMaps(THREE, cb) {
    var urls = window.NM_ASSETS || {};
    if (!urls.labelBody || !urls.labelMind) return;
    var loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    var maps = {}, n = 0, dead = false;
    ['body', 'mind'].forEach(function (k) {
      loader.load(k === 'body' ? urls.labelBody : urls.labelMind, function (t) {
        t.anisotropy = 4; maps[k] = t;
        if (++n === 2 && !dead) cb(maps);
      }, undefined, function () { dead = true; });
    });
  }

  /* ============================================================
     WAVE B — ANATOMY SCROLLYTELLING
     Desktop: the stage pins for 250% of scroll; the can spins and
     the camera dives while 4 callouts take turns. Mobile: no pin,
     auto-rotating can above a plain list. Any failure = show text.
     ============================================================ */
  function initScrub(isMobile) {
    var THREE = window.THREE;
    var section = document.querySelector('.nm-anatomy');
    if (!section) return;
    var stage = section.querySelector('.nm-anatomy-stage');
    var scene3d = section.querySelector('.nm-anatomy-scene');
    var callouts = Array.prototype.slice.call(section.querySelectorAll('.nm-anatomy-callout'));

    function showTextFallback() {
      callouts.forEach(function (c) { c.style.opacity = 1; c.style.pointerEvents = 'auto'; });
    }
    if (!THREE || !scene3d) { showTextFallback(); return; }

    loadMaps(THREE, function (maps) {
      var renderer;
      try {
        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      } catch (e) { showTextFallback(); return; }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2));
      renderer.setClearColor(0x000000, 0);
      scene3d.appendChild(renderer.domElement);

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 60);
      camera.position.set(0, 0.1, 9.8);
      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      var key = new THREE.DirectionalLight(0xffffff, 0.95);
      key.position.set(2.5, 3, 4); scene.add(key);
      var rimR = new THREE.PointLight(0xff2a2a, 1.0, 30); rimR.position.set(-4, 1.5, 2.5); scene.add(rimR);
      var rimC = new THREE.PointLight(0x00edef, 1.0, 30); rimC.position.set(4, -1, 2.5); scene.add(rimC);

      var env = makeStudioEnv(THREE, renderer);
      var cond = makeCondensation(THREE);
      var mode = (document.body.getAttribute('data-mode') === 'mind') ? 'mind' : 'body';
      var canGroup = null, baseRot = 0, setMode = function () {};
      function mount(group, base, setter) {
        baseRot = base; setMode = setter; canGroup = group;
        group.rotation.y = base; setMode(mode); scene.add(group);
      }
      function cylinderMount() {
        var mm = makeCanMesh(THREE, maps[mode], cond, env);
        mount(mm.group, -Math.PI * 2 * 0.62, function (m) { mm.bodyMat.map = maps[m]; mm.bodyMat.needsUpdate = true; });
      }
      if (!isMobile) {
        loadBothReal(THREE, env, function (real) {
          if (real && real.body && real.mind) {
            var holder = new THREE.Group();
            holder.add(real.body); holder.add(real.mind);
            mount(holder, 0, function (m) { real.body.visible = (m !== 'mind'); real.mind.visible = (m === 'mind'); });
          } else { cylinderMount(); }
        });
      } else { cylinderMount(); }

      /* follow the world */
      new MutationObserver(function () {
        mode = (document.body.getAttribute('data-mode') === 'mind') ? 'mind' : 'body';
        setMode(mode);
      }).observe(document.body, { attributes: true, attributeFilter: ['data-mode'] });

      var sizedW = 0, sizedH = 0;
      function resize() {
        var w = scene3d.clientWidth || 1, h = scene3d.clientHeight || 1;
        sizedW = w; sizedH = h;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      }
      resize();
      window.addEventListener('resize', resize);
      // the pin/resize combo can leave the canvas with stale dimensions
      // (user resizing mid-pin distorts the can) — observe the element itself
      if ('ResizeObserver' in window) new ResizeObserver(resize).observe(scene3d);

      var visible = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0 }).observe(section);
      }

      var drive = { turn: 0, zoom: 0 };   // written by the scrub timeline
      var clock = new THREE.Clock();
      var fr = 0;
      (function tick() {
        requestAnimationFrame(tick);
        if (!visible || document.hidden) return;
        // self-heal any missed size change (browser zoom, PiP, pinned refresh)
        if (++fr % 30 === 0 && (scene3d.clientWidth !== sizedW || scene3d.clientHeight !== sizedH)) resize();
        var t = clock.getElapsedTime();
        camera.position.z = 9.8 - drive.zoom;
        if (canGroup) {
          if (isMobile) canGroup.rotation.y += 0.006;
          else canGroup.rotation.y = baseRot + drive.turn;
          canGroup.position.y = Math.sin(t * 0.8) * 0.06;
        }
        renderer.render(scene, camera);
      })();

      /* desktop scrub choreography */
      if (!isMobile && window.gsap && window.ScrollTrigger) {
        var gsap = window.gsap;
        try {
          var tl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: 'top top',
              end: '+=200%',
              scrub: 0.5,
              pin: stage
              /* no anticipatePin — see the brand-story incident */
            }
          });
          // total 2.5 turns; camera dives mid-way then pulls back
          // (dive capped so the can never overflows the stage — a cropped
          //  cylinder reads as "stretched", reported by the merchant)
          tl.to(drive, { turn: Math.PI * 5, ease: 'none', duration: 10 }, 0)
            .to(drive, { zoom: 1.8, ease: 'power1.inOut', duration: 3 }, 1)
            .to(drive, { zoom: 0.7, ease: 'power1.inOut', duration: 3 }, 6.5);
          // clear the stage while the camera dives; title returns at the end
          var head = section.querySelector('.nm-anatomy-head');
          if (head) {
            tl.to(head, { autoAlpha: 0, y: -26, duration: 0.5 }, 0.25)
              .to(head, { autoAlpha: 1, y: 0, duration: 0.8 }, 8.6);
          }
          callouts.forEach(function (c, i) {
            var at = 0.6 + i * 2.3;
            var fromX = (i % 2 === 0) ? -46 : 46;
            tl.fromTo(c, { autoAlpha: 0, x: fromX }, { autoAlpha: 1, x: 0, duration: 0.9, ease: 'power2.out' }, at)
              .to(c, { autoAlpha: 0, x: -fromX * 0.4, duration: 0.7, ease: 'power2.in' }, at + 1.7);
          });
        } catch (e) { showTextFallback(); }
      } else if (!isMobile) {
        showTextFallback();
      }
    });

    /* if textures never arrive, the text must still show (desktop hides it) */
    setTimeout(function () {
      if (!scene3d.querySelector('canvas')) showTextFallback();
    }, 7000);
  }

  /* ============================================================
     WAVE C — PRODUCT PAGE: 360° viewer, pack stack, Android AR
     ============================================================ */
  /* load the designer's real can GLB; tune its all-metal materials so they
     actually catch light, and hand back a ready-to-clone group */
  function loadRealCan(THREE, url, env, cb, fail) {
    if (!THREE.GLTFLoader) { fail(); return; }
    new THREE.GLTFLoader().load(url, function (gltf) {
      var box = new THREE.Box3().setFromObject(gltf.scene);
      var size = new THREE.Vector3(); box.getSize(size);
      var center = new THREE.Vector3(); box.getCenter(center);
      gltf.scene.position.sub(center);              // center at origin
      gltf.scene.rotation.y = Math.PI;              // logo faces the camera (label back is at +Z in the export)
      var group = new THREE.Group();
      group.add(gltf.scene);
      var s = 3.8 / size.y;                          // normalize to scene units
      group.scale.setScalar(s);
      group.traverse(function (o) {
        if (!o.isMesh || !o.material) return;
        var m = o.material;
        m.envMap = env;
        if (m.map) {                                 // the printed label
          m.metalness = 0.45;
          m.roughness = Math.min(m.roughness, 0.5);
          m.envMapIntensity = 0.7;
        } else {                                     // bare aluminium parts
          m.metalness = 0.95;
          m.roughness = 0.3;
          m.envMapIntensity = 1.1;
        }
        m.needsUpdate = true;
      });
      cb(group);
    }, undefined, function () { fail(); });
  }

  /* load BOTH real cans for one scene's env (hero & anatomy need to switch) */
  function loadBothReal(THREE, env, cb) {
    var a = window.NM_ASSETS || {};
    if (!a.realBody || !a.realMind || !THREE.GLTFLoader) { cb(null); return; }
    var out = {}, n = 0, dead = false;
    function step() { if (n === 2) cb(dead ? null : out); }
    loadRealCan(THREE, a.realBody, env, function (g) { out.body = g; n++; step(); }, function () { dead = true; n++; step(); });
    loadRealCan(THREE, a.realMind, env, function (g) { out.mind = g; n++; step(); }, function () { dead = true; n++; step(); });
  }

  /* ---------- shared 3D helpers (hero can + split cans) ---------- */

  /* studio environment: dark room, two softboxes, red & cyan panels —
     gives the aluminium and label real reflections */
  function makeStudioEnv(THREE, renderer) {
    var c = document.createElement('canvas');
    c.width = 256; c.height = 128;
    var g = c.getContext('2d');
    var grad = g.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0, '#3a3a44'); grad.addColorStop(0.5, '#101018'); grad.addColorStop(1, '#05050a');
    g.fillStyle = grad; g.fillRect(0, 0, 256, 128);
    g.fillStyle = 'rgba(255,255,255,0.92)';
    g.fillRect(30, 10, 36, 70); g.fillRect(150, 14, 30, 60);
    g.fillStyle = 'rgba(255,42,42,0.55)'; g.fillRect(210, 30, 26, 50);
    g.fillStyle = 'rgba(0,237,239,0.55)'; g.fillRect(95, 25, 20, 55);
    var tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    var pm = new THREE.PMREMGenerator(renderer);
    pm.compileEquirectangularShader();
    var env = pm.fromEquirectangular(tex).texture;
    tex.dispose(); pm.dispose();
    return env;
  }

  /* condensation droplets: bump (raised) + roughness (glossy spots) maps */
  function makeCondensation(THREE) {
    function base(fill) {
      var c = document.createElement('canvas');
      c.width = 512; c.height = 256;
      var g = c.getContext('2d');
      g.fillStyle = fill; g.fillRect(0, 0, 512, 256);
      return { c: c, g: g };
    }
    var b = base('#808080'), r = base('#ffffff');
    for (var i = 0; i < 520; i++) {
      var x = Math.random() * 512, y = Math.random() * 256, rad = 0.6 + Math.random() * 2.6;
      var gb = b.g.createRadialGradient(x, y, 0, x, y, rad);
      gb.addColorStop(0, '#cacaca'); gb.addColorStop(0.7, '#9a9a9a'); gb.addColorStop(1, '#808080');
      b.g.fillStyle = gb; b.g.beginPath(); b.g.arc(x, y, rad, 0, 7); b.g.fill();
      var gr = r.g.createRadialGradient(x, y, 0, x, y, rad);
      gr.addColorStop(0, '#3c3c3c'); gr.addColorStop(1, '#ffffff');
      r.g.fillStyle = gr; r.g.beginPath(); r.g.arc(x, y, rad, 0, 7); r.g.fill();
    }
    var bump = new THREE.CanvasTexture(b.c), rough = new THREE.CanvasTexture(r.c);
    bump.wrapS = bump.wrapT = rough.wrapS = rough.wrapT = THREE.RepeatWrapping;
    bump.repeat.set(2, 1); rough.repeat.set(2, 1);
    return { bump: bump, rough: rough };
  }

  /* full can mesh with premium finish */
  function makeCanMesh(THREE, map, cond, env) {
    var R = 0.72, H = 3.32;
    var group = new THREE.Group();
    var bodyMat = new THREE.MeshStandardMaterial({
      map: map, metalness: 0.35, roughness: 0.55,
      envMap: env, envMapIntensity: 0.55,
      bumpMap: cond.bump, bumpScale: 0.4, roughnessMap: cond.rough
    });
    group.add(new THREE.Mesh(new THREE.CylinderGeometry(R, R, H, 72, 1, true), bodyMat));
    var alu = new THREE.MeshStandardMaterial({ color: 0xc9c9d1, metalness: 0.9, roughness: 0.28, envMap: env, envMapIntensity: 1.1 });
    var aluDark = new THREE.MeshStandardMaterial({ color: 0x84848e, metalness: 0.9, roughness: 0.35, envMap: env, envMapIntensity: 0.9 });
    var tt = new THREE.Mesh(new THREE.CylinderGeometry(0.585, R, 0.24, 72, 1, true), alu);
    tt.position.y = H / 2 + 0.12; group.add(tt);
    var lid = new THREE.Mesh(new THREE.CircleGeometry(0.585, 72), aluDark);
    lid.rotation.x = -Math.PI / 2; lid.position.y = H / 2 + 0.24; group.add(lid);
    var bt = new THREE.Mesh(new THREE.CylinderGeometry(R, 0.6, 0.18, 72, 1, true), alu);
    bt.position.y = -H / 2 - 0.09; group.add(bt);
    return { group: group, bodyMat: bodyMat };
  }

  /* radial particle burst fired on world switch */
  function makeBurst(THREE, scene) {
    var N = 140;
    var geo = new THREE.BufferGeometry();
    var pos = new Float32Array(N * 3), vel = new Float32Array(N * 3);
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    var mat = new THREE.PointsMaterial({ color: 0xff2a2a, size: 0.06, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
    var pts = new THREE.Points(geo, mat);
    pts.frustumCulled = false;
    scene.add(pts);
    var life = 0;
    return {
      fire: function (color) {
        mat.color.setHex(color);
        for (var i = 0; i < N; i++) {
          var a = Math.random() * Math.PI * 2, y = (Math.random() - 0.5) * 3.2;
          pos[i * 3] = Math.cos(a) * 0.75; pos[i * 3 + 1] = y; pos[i * 3 + 2] = Math.sin(a) * 0.75;
          var sp = 0.04 + Math.random() * 0.07;
          vel[i * 3] = Math.cos(a) * sp; vel[i * 3 + 1] = (Math.random() - 0.3) * 0.03; vel[i * 3 + 2] = Math.sin(a) * sp;
        }
        geo.attributes.position.needsUpdate = true;
        life = 1; mat.opacity = 0.95;
      },
      update: function () {
        if (life <= 0) return;
        life -= 0.02;
        mat.opacity = Math.max(0, life * 0.95);
        for (var i = 0; i < N; i++) {
          pos[i * 3] += vel[i * 3]; pos[i * 3 + 1] += vel[i * 3 + 1]; pos[i * 3 + 2] += vel[i * 3 + 2];
        }
        geo.attributes.position.needsUpdate = true;
      }
    };
  }

  /* two slow counter-rotating 3D cans replacing the split-section PNGs */
  function initSplitCans(THREE, maps, cond) {
    var panels = document.querySelectorAll('.nm-split-panel');
    if (panels.length !== 2) return;
    ['body', 'mind'].forEach(function (mode, i) {
      var panel = panels[i];
      var img = panel.querySelector('.nm-split-can');
      if (!img) return;
      var renderer;
      try {
        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      } catch (e) { return; }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
      renderer.setClearColor(0x000000, 0);
      var cv = renderer.domElement;
      cv.style.cssText = 'position:absolute;pointer-events:none;z-index:2;';
      if (!panel.style.position) panel.style.position = 'relative';
      panel.appendChild(cv);

      var env = makeStudioEnv(THREE, renderer);
      var scene = new THREE.Scene();
      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      var key = new THREE.DirectionalLight(0xffffff, 0.9);
      key.position.set(2.5, 3, 4); scene.add(key);
      var rim = new THREE.PointLight(mode === 'mind' ? 0x00edef : 0xff2a2a, 1.25, 30);
      rim.position.set(i ? 4 : -4, 1.2, 2.6); scene.add(rim);
      var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 50);
      camera.position.set(0, 0.1, 7.8);

      var can = null;
      function mountCan(group, baseY) { group.rotation.y = baseY; can = group; scene.add(group); }
      var realUrl = mode === 'mind' ? (window.NM_ASSETS || {}).realMind : (window.NM_ASSETS || {}).realBody;
      if (realUrl && THREE.GLTFLoader) {
        loadRealCan(THREE, realUrl, env, function (g) { mountCan(g, 0); },
          function () { mountCan(makeCanMesh(THREE, maps[mode], cond, env).group, -Math.PI * 2 * 0.62); });
      } else {
        mountCan(makeCanMesh(THREE, maps[mode], cond, env).group, -Math.PI * 2 * 0.62);
      }

      // transform-free box of the hidden PNG (the entrance scrub translates it,
      // so getBoundingClientRect would place the canvas over the text below)
      function offsetBox(el, ancestor) {
        var x = 0, y = 0, n = el;
        while (n && n !== ancestor) { x += n.offsetLeft; y += n.offsetTop; n = n.offsetParent; }
        return { left: x, top: y, width: el.offsetWidth, height: el.offsetHeight };
      }
      var glow = panel.querySelector('.nm-split-panel-glow');
      var lastKey = '';
      function place() {
        var ib = offsetBox(img, panel);
        var w = ib.width > 20 ? ib.width : Math.min(panel.clientWidth * 0.45, 240);
        var h = ib.height > 20 ? ib.height : Math.min(panel.clientHeight * 0.4, 320);
        var left = (ib.width > 20 ? ib.left + ib.width / 2 : panel.clientWidth / 2) - w / 2;
        var top = (ib.height > 20 ? ib.top + ib.height / 2 : panel.clientHeight * 0.35) - h / 2;
        var key = left + '/' + top + '/' + w + '/' + h;
        if (key === lastKey) return;   // panels flex on hover: only touch DOM on change
        lastKey = key;
        cv.style.left = left + 'px'; cv.style.top = top + 'px';
        cv.style.width = w + 'px'; cv.style.height = h + 'px';
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        // keep the ambient glow glued behind the can
        if (glow) {
          var gw = w * 2.1, gh = h * 1.35;
          glow.style.left = (left + w / 2 - gw / 2) + 'px';
          glow.style.top = (top + h / 2 - gh / 2) + 'px';
          glow.style.width = gw + 'px';
          glow.style.height = gh + 'px';
          glow.style.right = 'auto'; glow.style.bottom = 'auto';
        }
      }
      place();
      window.addEventListener('resize', place);
      if ('ResizeObserver' in window) new ResizeObserver(place).observe(panel);
      img.style.visibility = 'hidden';

      /* hover life: spin up, grow, tilt toward the cursor, light up */
      var hov = 0, tiltX = 0, tiltZ = 0, curTiltX = 0, curTiltZ = 0, spin = 0.005, curSpin = 0.005;
      var baseRim = rim.intensity;
      panel.addEventListener('mouseenter', function () { hov = 1; });
      panel.addEventListener('mouseleave', function () { hov = 0; tiltX = 0; tiltZ = 0; });
      panel.addEventListener('mousemove', function (e) {
        var r = panel.getBoundingClientRect();
        tiltZ = ((e.clientX - r.left) / r.width - 0.5) * -0.16;
        tiltX = ((e.clientY - r.top) / r.height - 0.5) * 0.12;
      });

      var visible = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0 }).observe(panel);
      }
      var clock = new THREE.Clock();
      var frame = 0;
      (function tick() {
        requestAnimationFrame(tick);
        if (!visible || document.hidden) return;
        if (++frame % 10 === 0) place();   // follow the panel's hover flex
        if (!can) { renderer.render(scene, camera); return; }  // still loading
        var t = clock.getElapsedTime();
        curSpin += (((hov ? 0.014 : 0.005) * (i ? -1 : 1)) - curSpin) * 0.06;
        can.rotation.y += curSpin;
        curTiltX += (tiltX - curTiltX) * 0.08;
        curTiltZ += (tiltZ - curTiltZ) * 0.08;
        can.rotation.x = curTiltX;
        can.rotation.z = curTiltZ + Math.sin(t * 0.6) * 0.015;
        var baseScale = can.userData.nmScale || (can.userData.nmScale = can.scale.x);
        var s = baseScale * (1 + (hov ? 0.07 : 0));
        can.scale.x += (s - can.scale.x) * 0.08;
        can.scale.y = can.scale.z = can.scale.x;
        rim.intensity += ((hov ? baseRim * 1.9 : baseRim) - rim.intensity) * 0.08;
        can.position.y = Math.sin(t * 0.8 + i * 2) * 0.07;
        renderer.render(scene, camera);
      })();
    });
  }

  /* ============================================================
     REAL 3D CAN — label textures wrapped on a cylinder.
     Replaces the flat hero PNGs only once both textures load;
     any failure leaves the 2D cans untouched.
     ============================================================ */
  function initCan3D(hero, isMobile) {
    /* Mobile Safari kills or purges WebGL canvases unpredictably (context
       drops, background buffer purges) — reproduced on WebKit: the can
       renders a few frames then goes blank forever while the PNGs are
       already hidden. The product PNGs are 100% reliable, so the hero 3D
       can is a DESKTOP-ONLY luxury. Mobile keeps photos + particles. */
    if (isMobile) return;
    var THREE = window.THREE;
    var urls = window.NM_ASSETS || {};
    if (!urls.labelBody || !urls.labelMind) return;
    var wrapper = hero && hero.querySelector('.nm-can-wrapper');
    if (!wrapper) return;

    var loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    var maps = {};
    var loaded = 0, failed = false;

    ['body', 'mind'].forEach(function (k) {
      loader.load(
        k === 'body' ? urls.labelBody : urls.labelMind,
        function (tex) {
          tex.anisotropy = 4;
          maps[k] = tex;
          if (++loaded === 2 && !failed) build();
        },
        undefined,
        function () { failed = true; }
      );
    });

    function build() {
      var renderer;
      try {
        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      } catch (e) { return; }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0x000000, 0);
      var cv = renderer.domElement;
      cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:3;';
      wrapper.style.position = 'relative';
      wrapper.appendChild(cv);

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50);
      camera.position.set(0, 0.15, 7.6);

      /* lights: studio white + world-colored rims */
      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      var key = new THREE.DirectionalLight(0xffffff, 0.9);
      key.position.set(2.5, 3, 4);
      scene.add(key);
      var rimR = new THREE.PointLight(0xff2a2a, 1.1, 30);
      rimR.position.set(-4, 1.5, 2.5);
      scene.add(rimR);
      var rimC = new THREE.PointLight(0x00edef, 1.1, 30);
      rimC.position.set(4, -1, 2.5);
      scene.add(rimC);

      var env = makeStudioEnv(THREE, renderer);
      var cond = makeCondensation(THREE);
      var burst = makeBurst(THREE, scene);

      /* --- CAN OBJECT: uniform {group, setMode} for cylinder OR real GLB --- */
      function cylinderCan() {
        var R = 0.72, H = 3.32;
        var can = new THREE.Group();
        var bodyMat = new THREE.MeshStandardMaterial({
          map: maps[(document.body.getAttribute('data-mode') === 'mind') ? 'mind' : 'body'],
          metalness: 0.35, roughness: 0.55,
          envMap: env, envMapIntensity: 0.55,
          bumpMap: cond.bump, bumpScale: 0.4, roughnessMap: cond.rough
        });
        can.add(new THREE.Mesh(new THREE.CylinderGeometry(R, R, H, 72, 1, true), bodyMat));
        var alu = new THREE.MeshStandardMaterial({ color: 0xc9c9d1, metalness: 0.9, roughness: 0.28, envMap: env, envMapIntensity: 1.1 });
        var aluDark = new THREE.MeshStandardMaterial({ color: 0x84848e, metalness: 0.9, roughness: 0.35, envMap: env, envMapIntensity: 0.9 });
        var tt = new THREE.Mesh(new THREE.CylinderGeometry(0.585, R, 0.24, 72, 1, true), alu); tt.position.y = H / 2 + 0.12; can.add(tt);
        var lid = new THREE.Mesh(new THREE.CircleGeometry(0.585, 72), aluDark); lid.rotation.x = -Math.PI / 2; lid.position.y = H / 2 + 0.24; can.add(lid);
        var bt = new THREE.Mesh(new THREE.CylinderGeometry(R, 0.6, 0.18, 72, 1, true), alu); bt.position.y = -H / 2 - 0.09; can.add(bt);
        can.rotation.y = -Math.PI * 2 * 0.62;
        return { group: can, setMode: function (m) { bodyMat.map = maps[m]; bodyMat.needsUpdate = true; } };
      }
      function realCan(real) {
        var holder = new THREE.Group();
        holder.add(real.body); holder.add(real.mind);
        return { group: holder, setMode: function (m) { real.body.visible = (m !== 'mind'); real.mind.visible = (m === 'mind'); } };
      }

      function finish(canObj) {
        var can = canObj.group;
        scene.add(can);

        /* the versus section gets real cans too (desktop only) */
        if (!isMobile) initSplitCans(THREE, maps, cond);

        /* swap the flat PNGs out ONLY once the 3D has actually rendered a few
           frames — on some mobile GPUs (iOS Safari) WebGL mounts then dies,
           which used to leave the hero with NO can at all. The context-lost
           listener flips the PNGs back if the GPU gives up later. */
        var style = document.createElement('style');
        style.textContent = '.nm-3dcan-on .nm-hs-can{visibility:hidden!important}';
        document.head.appendChild(style);
        var renderedFrames = 0, canvasLive = false, ctxLost = false;
        renderer.domElement.addEventListener('webglcontextlost', function (e) {
          e.preventDefault();
          ctxLost = true;
          hero.classList.remove('nm-3dcan-on');   // PNG fallback returns
        });
        renderer.domElement.addEventListener('webglcontextrestored', function () {
          ctxLost = false;
          hero.classList.add('nm-3dcan-on');
        });

        /* world switch: half-spin + model/texture swap mid-flight */
        var currentMode = (document.body.getAttribute('data-mode') === 'mind') ? 'mind' : 'body';
        canObj.setMode(currentMode);
        function switchTo(mode) {
          if (mode === currentMode) return;
          currentMode = mode;
          burst.fire(mode === 'mind' ? 0x00edef : 0xff2a2a);
          if (window.gsap) {
            var tl = window.gsap.timeline();
            tl.to(can.rotation, { y: '+=' + Math.PI, duration: 0.45, ease: 'power2.in' })
              .add(function () { canObj.setMode(mode); })
              .to(can.rotation, { y: '+=' + Math.PI, duration: 0.55, ease: 'power3.out' })
              .fromTo(can.scale, { x: 0.94, y: 0.94, z: 0.94 }, { x: 1, y: 1, z: 1, duration: 0.5, ease: 'back.out(2)' }, '-=0.4');
          } else {
            canObj.setMode(mode);
          }
        }
        new MutationObserver(function () {
          switchTo(hero.getAttribute('data-active-product') === 'mind' ? 'mind' : 'body');
        }).observe(hero, { attributes: true, attributeFilter: ['data-active-product'] });
        switchTo(hero.getAttribute('data-active-product') === 'mind' ? 'mind' : 'body');

        /* drag to spin (adds to the slow auto-spin) */
        var dragVel = 0, dragging = false, lastX = 0;
        var area = hero.querySelector('.nm-can-scene') || wrapper;
        area.addEventListener('pointerdown', function (e) { dragging = true; lastX = e.clientX; });
        window.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          dragVel += (e.clientX - lastX) * 0.004;
          lastX = e.clientX;
        });
        window.addEventListener('pointerup', function () {
          /* easter egg: a hard flick reaches "MACH 9" (toast lives in energy-fx) */
          if (dragging && Math.abs(dragVel) > 0.32) {
            try { window.dispatchEvent(new CustomEvent('nm:mach9')); } catch (err) {}
          }
          dragging = false;
        });

        function resize() {
          var w = wrapper.clientWidth || 1, h = wrapper.clientHeight || 1;
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
        }
        resize();
        window.addEventListener('resize', resize);
        if ('ResizeObserver' in window) new ResizeObserver(resize).observe(wrapper);

        var visible = true;
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0 })
            .observe(wrapper);
        }

        var clock = new THREE.Clock();
        (function tick() {
          requestAnimationFrame(tick);
          if (!visible || document.hidden) return;
          var t = clock.getElapsedTime();
          can.rotation.y += 0.0035 + dragVel;
          dragVel *= 0.94;
          can.position.y = Math.sin(t * 0.9) * 0.09;      /* float */
          can.rotation.z = Math.sin(t * 0.6) * 0.02;       /* gentle sway */
          burst.update();
          renderer.render(scene, camera);
          if (!ctxLost && !canvasLive && ++renderedFrames >= 3) { /* proven alive → swap PNGs out */
            canvasLive = true;
            hero.classList.add('nm-3dcan-on');
          }
        })();
      }

      /* real designer GLB on desktop; light cylinder on mobile / on failure */
      if (!isMobile) {
        loadBothReal(THREE, env, function (real) {
          finish(real && real.body && real.mind ? realCan(real) : cylinderCan());
        });
      } else {
        finish(cylinderCan());
      }
    }
  }

  /* THREE loads from CDN just before this file; poll briefly if it lags */
  var tries = 0;
  (function waitForThree() {
    if (window.THREE) { onReady(bootAll); return; }
    if (++tries > 40) return;
    setTimeout(waitForThree, 150);
  })();

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }
})();
